/**
 * TileMap.ts
 * Tile grid, terrain types, and elevation. The town starts mostly locked and
 * unlocks organically as reputation grows (§3.1).
 *
 * Rendering is 2.5D isometric: gameplay still reasons in (col, row) grid space,
 * but world-space coordinates are an isometric projection with per-tile height.
 *
 * Generation is seeded: each new game picks a random 32-bit seed, and every
 * per-tile hash and layout parameter is derived from it.  The seed is stored in
 * the save file so loaded games regenerate the same map.
 */

import { TerrainType, type CorporateHQLayout, type GridPosition, type VenueLayout, type WorldPosition } from '../types';
import {
  ISO_TILE_WIDTH,
  ISO_TILE_HEIGHT,
  ELEVATION_STEP,
  GROUND_LEVELS,
} from '../constants';
import { getMapSize } from '../mapConfig';
import { generateMapData, type MapGenData } from './mapGen';

const HW = ISO_TILE_WIDTH / 2;
const HH = ISO_TILE_HEIGHT / 2;

export class TileMap {
  readonly width:  number;
  readonly height: number;

  /** Top-left tile of the 2×2 Future Beacon, at the heart of the map (§3.2). */
  readonly beacon: GridPosition;

  /**
   * 32-bit seed that drives all map randomness.
   * Generated fresh for a new game; stored in the save so loaded games
   * reconstruct the identical map.
   */
  readonly seed: number;

  /**
   * Four venue positions (NW, NE, SW, SE quadrants) generated from the seed.
   * BuildSystem reads these instead of hardcoded coordinates.
   */
  readonly venueLayouts: VenueLayout[] = [];
  /** Hidden enemy estate: a remote Corporate HQ / fake-pop factory. */
  corporateHQ: CorporateHQLayout = {
    buildingCol: 0,
    buildingRow: 0,
    accessCol: 0,
    accessRow: 0,
    spawnTiles: [],
  };

  /**
   * Five band-member starting positions scattered around the Beacon,
   * generated from the seed. Game.ts reads these in spawnBandMembers().
   */
  readonly bandSpawnPositions: GridPosition[] = [];

  private readonly terrain: TerrainType[] = [];
  private readonly variant: number[] = []; // per-tile colour variation seed

  // ── Seeded layout parameters (set once during generate()) ────────────────
  private hillCol    = 32;
  private hillRow    = 7;
  private hillRadius = 4;

  constructor(seed?: number, genData?: MapGenData) {
    const size  = getMapSize();
    this.width  = size.width;
    this.height = size.height;
    this.beacon = {
      col: Math.floor(size.width  / 2) - 1,
      row: Math.floor(size.height / 2) - 1,
    };
    this.seed = seed !== undefined ? (seed >>> 0) || 1
                                   : (Math.random() * 0x7FFFFFFF | 0) || 1;
    this.applyGenData(genData ?? generateMapData(this.seed, this.width, this.height));
  }

  /**
   * Async factory — runs map generation in a Web Worker so the main thread
   * stays responsive during the ~500ms boot compute.
   * Falls back to synchronous generation if the Worker fails.
   */
  static async createAsync(seed?: number): Promise<TileMap> {
    const resolvedSeed = seed !== undefined ? (seed >>> 0) || 1
                                            : (Math.random() * 0x7FFFFFFF | 0) || 1;
    const size = getMapSize();

    return new Promise<TileMap>((resolve) => {
      try {
        const worker = new Worker(
          new URL('../workers/map.worker.ts', import.meta.url),
          { type: 'module' },
        );
        const timeout = window.setTimeout(() => {
          worker.terminate();
          resolve(new TileMap(resolvedSeed)); // synchronous fallback
        }, 8000);
        worker.onmessage = (e: MessageEvent<MapGenData>) => {
          clearTimeout(timeout);
          worker.terminate();
          resolve(new TileMap(resolvedSeed, e.data));
        };
        worker.onerror = () => {
          clearTimeout(timeout);
          worker.terminate();
          resolve(new TileMap(resolvedSeed)); // synchronous fallback
        };
        worker.postMessage({ seed: resolvedSeed, width: size.width, height: size.height });
      } catch {
        resolve(new TileMap(resolvedSeed)); // synchronous fallback
      }
    });
  }

  // ── Public queries ─────────────────────────────────────────────────────

  getTerrain(pos: GridPosition): TerrainType {
    if (!this.inBounds(pos.col, pos.row)) return TerrainType.Locked;
    return this.terrain[this.idx(pos.col, pos.row)];
  }

  getVariant(col: number, row: number): number {
    if (!this.inBounds(col, row)) return 0;
    return this.variant[this.idx(col, row)];
  }

  setTerrain(pos: GridPosition, terrain: TerrainType): void {
    if (this.inBounds(pos.col, pos.row)) {
      this.terrain[this.idx(pos.col, pos.row)] = terrain;
    }
  }

  isPassable(pos: GridPosition): boolean {
    // The 2×2 Beacon is permanently impassable — nothing crosses it.
    if (this.isBeacon(pos.col, pos.row)) return false;
    const t = this.getTerrain(pos);
    return t === TerrainType.Grass || t === TerrainType.Path || t === TerrainType.Corporate;
  }

  isBeacon(col: number, row: number): boolean {
    return (
      col >= this.beacon.col &&
      col <= this.beacon.col + 1 &&
      row >= this.beacon.row &&
      row <= this.beacon.row + 1
    );
  }

  isCorporateHQ(col: number, row: number): boolean {
    return (
      col >= this.corporateHQ.buildingCol - 1 &&
      col <= this.corporateHQ.buildingCol + 1 &&
      row >= this.corporateHQ.buildingRow - 1 &&
      row <= this.corporateHQ.buildingRow + 1
    );
  }

  inBounds(col: number, row: number): boolean {
    return col >= 0 && col < this.width && row >= 0 && row < this.height;
  }

  // ── Elevation ───────────────────────────────────────────────────────────

  /** Ground (terrain) height in levels — water sits in a basin, grass can hill. */
  groundHeight(col: number, row: number): number {
    const t = this.getTerrain({ col, row });
    if (t === TerrainType.Water) return 0;
    let h = GROUND_LEVELS;
    // A stepped hill blob in the NE grassland — position and radius vary per seed.
    if (t === TerrainType.Grass) {
      const dx = (col - this.hillCol) / this.hillRadius;
      const dy = (row - this.hillRow) / this.hillRadius;
      const d = dx * dx + dy * dy;
      if (d < 1) h += Math.round((1 - d) * 2);
    }
    return h;
  }

  /**
   * Human-readable 8-character hex code derived from the map seed, formatted
   * as XXXX-XXXX.  Unique per seed, can be shared to replay the same map.
   */
  get mapCode(): string {
    const h = (this.seed >>> 0).toString(16).toUpperCase().padStart(8, '0');
    return `${h.slice(0, 4)}-${h.slice(4)}`;
  }

  /** Extra height for structures sitting on the ground (drawn as raised blocks). */
  structHeight(col: number, row: number): number {
    if (this.isBeacon(col, row)) return 3;
    if (this.isCorporateHQ(col, row)) return 3;
    if (this.getTerrain({ col, row }) === TerrainType.Building) return 2;
    if (this.getTerrain({ col, row }) === TerrainType.Obstacle) return 1;
    return 0;
  }

  /** Total height of a tile's top face, in levels. */
  elevation(col: number, row: number): number {
    return this.groundHeight(col, row) + this.structHeight(col, row);
  }

  // ── Isometric coordinate conversion ──────────────────────────────────────

  /** Project a (possibly fractional) grid cell to its ground-plane iso point. */
  tileBase(col: number, row: number): WorldPosition {
    return { x: (col - row) * HW, y: (col + row) * HH };
  }

  /** World point at the centre of a tile's elevated top face. */
  tileTopCenter(col: number, row: number): WorldPosition {
    const b = this.tileBase(col, row);
    return { x: b.x, y: b.y - this.elevation(col, row) * ELEVATION_STEP };
  }

  /** Where a unit standing on this tile is drawn (its elevated top centre). */
  gridToWorld(pos: GridPosition): WorldPosition {
    return this.tileTopCenter(pos.col, pos.row);
  }

  /**
   * Convert a world point to a grid cell. Picking is resolved on the *land top*
   * plane (the common ground level), so hovering flat ground is exact; tall
   * hills/buildings read a tile or so low — acceptable until terrain picking
   * needs to be height-aware.
   */
  worldToGrid(pos: WorldPosition): GridPosition {
    const wy = pos.y + GROUND_LEVELS * ELEVATION_STEP;
    const a = pos.x / HW;
    const b = wy / HH;
    return {
      col: Math.floor((a + b) / 2),
      row: Math.floor((b - a) / 2),
    };
  }

  /** World-space centre of the 2×2 Future Beacon's elevated top. */
  beaconCenter(): WorldPosition {
    const b = this.tileBase(this.beacon.col + 0.5, this.beacon.row + 0.5);
    const levels = GROUND_LEVELS + 3; // land + beacon struct
    return { x: b.x, y: b.y - levels * ELEVATION_STEP };
  }

  // ── Generation ──────────────────────────────────────────────────────────

  private idx(col: number, row: number): number {
    return row * this.width + col;
  }

  private set(col: number, row: number, t: TerrainType): void {
    if (this.inBounds(col, row)) this.terrain[this.idx(col, row)] = t;
  }

  private applyGenData(data: MapGenData): void {
    for (let i = 0; i < data.terrain.length; i++) this.terrain[i] = data.terrain[i];
    for (let i = 0; i < data.variant.length;  i++) this.variant[i]  = data.variant[i];
    this.hillCol    = data.hillCol;
    this.hillRow    = data.hillRow;
    this.hillRadius = data.hillRadius;
    this.venueLayouts.push(...data.venueLayouts);
    this.corporateHQ = data.corporateHQ;
    this.bandSpawnPositions.push(...data.bandSpawnPositions);
  }

}
